import React, { useCallback, useEffect, useState } from "react";

import {
  downloadSharesCertificatePdf,
  fetchShareCertificatePreview,
} from "../../services/investmentService";
import { projectTitleWithCode } from "../../utils/projectViews";
import ShareCertificateDocument from "./ShareCertificateDocument";
import "../../share-certificate.css";

export default function ShareCertificatePreviewModal({ project, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const projectLabel = project ? projectTitleWithCode(project) : "Project";

  useEffect(() => {
    if (!project?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    fetchShareCertificatePreview(project.id)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const d = err?.response?.data?.detail;
          setError(typeof d === "string" ? d : "Could not load share certificate.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  const handleDownloadPdf = useCallback(async () => {
    if (!project?.id || downloading) return;
    setDownloading(true);
    try {
      await downloadSharesCertificatePdf(project.id, project.project_code);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not download PDF.");
    } finally {
      setDownloading(false);
    }
  }, [project, downloading]);

  if (!project?.id) return null;

  return (
    <div className="invx-about-modal-overlay share-cert-preview-overlay" onClick={onClose} role="presentation">
      <div
        className="invx-about-modal share-cert-preview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="share-cert-preview-title"
        aria-modal="true"
      >
        <button type="button" className="invx-about-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 id="share-cert-preview-title" style={{ marginTop: 0 }}>
          Share certificate
        </h3>
        <p className="invx-about-modal-project muted" style={{ marginBottom: "0.75rem" }}>
          {projectLabel}
        </p>

        {loading ? <p className="muted share-cert-preview-status">Loading certificate…</p> : null}
        {error ? (
          <p className="msg msg-error share-cert-preview-status" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && data ? (
          <div className="share-cert-preview-body">
            <ShareCertificateDocument data={data} printId="share-certificate-print" />
          </div>
        ) : null}

        <div className="share-cert-modal-toolbar no-print">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={loading || !data?.valid}
            onClick={() => window.print()}
          >
            Print
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={loading || downloading || !data}
            onClick={handleDownloadPdf}
          >
            {downloading ? "Downloading…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
